/**
 * Interview-related enums matching the API contracts
 */

export enum InterviewType {
  EXIT_INTERVIEW = "exit_interview",
  PULSE_CHECK = "pulse_check",
  ONBOARDING = "onboarding",
}

/**
 * Interview definition status (for managing interview templates)
 */
export enum InterviewStatus {
  DRAFT = "draft",
  PUBLISHED = "published",
  ARCHIVED = "archived",
}

/**
 * Frontend-specific interview session UI status
 * These map to the UI state machine, not the backend session status
 */
export enum InterviewUIStatus {
  PENDING = "pending",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
}

export enum InterviewSessionStatus {
  NEW = "new",
  COMPLETE = "complete",
  ERROR = "error",
  EMPTY = "empty",
}

export enum MessageRole {
  USER = "user",
  ASSISTANT = "assistant",
  SYSTEM = "system",
}

export enum InterviewPhase {
  LANGUAGE_SELECTION = "language_selection",
  INTRODUCTION = "introduction",
  QUESTIONS = "questions",
  FOLLOWUP = "followup",
  CLOSING = "closing",
  COMPLETED = "completed",
}

export enum InterviewQuestionInputType {
  PHONE = "phone",
  FILE_UPLOAD = "file_upload",
  TEXT = "text",
  LONG_TEXT = "long_text",
  SINGLE_CHOICE = "single_choice",
  MULTIPLE_CHOICE = "multiple_choice",
  YES_NO = "yes_no",
  NUMERICAL = "numerical",
  SLIDER = "slider",
  VOICE = "voice",
}

/**
 * Response format for assessment questions. Mirrors the backend enum
 * (services/api/screener/src/common/enums/assessment-question-response-format.enum.ts).
 */
export enum ResponseFormat {
  Written = "written",
  VoiceRecommended = "voice_recommended",
  VoiceRequired = "voice_required",
}

/**
 * Presigned S3 URLs for voice question recording uploads + playback.
 * Issued by the orchestrator (POST /interview/:sessionId/audio/upload-url).
 */
export interface VoiceUrls {
  uploadUrl: string;
  audioUrl: string;
}

/**
 * Request/loading states for messages
 */
export enum RequestStates {
  Loading = "loading",
  Success = "success",
  Error = "error",
}

/**
 * Simplified option interface for Pusher events
 * Used for choice-type questions (single_choice, multiple_choice, yes_no)
 */
export interface InputOption {
  id: string;
  optionText: string;
  optionValue?: string;
  sortOrder: number;
}

/**
 * Message in a session
 */
export interface Message {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: Date;
  /** Development-only correlation for local benchmark traces. */
  benchmarkTurnId?: string;
  questionId?: string;
  messageOrder?: number;
  metadata?: Record<string, unknown>;
  /** The input type expected for the user's response to this message */
  inputType?: InterviewQuestionInputType;
  /** Options for choice-type questions */
  options?: InputOption[];
  /**
   * SRN-715: classification of the current question (echoed back when the
   * user replies so user-turn rows have matching `question_type`).
   */
  questionType?: InterviewMessageQuestionType;
  /** Loading/success/error state for this message */
  status?: RequestStates;
  /** Candidate-selected language for multilingual sessions */
  language?: string;
  /** Presigned S3 upload URL for voice questions */
  uploadUrl?: string;
  /** S3 key returned alongside the upload URL — sent back as the user's "answer" */
  audioUrl?: string;
  /** Voice response format (voice_required, voice_recommended, written) */
  responseFormat?: ResponseFormat;
  /** How this user message was answered; set on resumed transcript rows. */
  answerResponseFormat?: InterviewMessageResponseFormat | null;
  /** Stored audio key for a prior voice answer; set on resumed transcript rows. */
  audioS3Key?: string | null;
  /** Whether the candidate can skip this voice question */
  canSkip?: boolean;
  /** Maximum recording duration in seconds */
  recordingDurationSeconds?: number;
  /**
   * AS-1564: 1-based candidate-facing progress position. Hits `totalQuestions + 1`
   * at the closing/completed phase so the ProgressBar fill formula reaches 100%.
   * Only set on assistant turns from the QUESTIONS phase onward.
   */
  currentQuestionNumber?: number;
  /** AS-1564: total counted questions for the interview. */
  totalQuestions?: number;
}

/**
 * Interview session
 */
export interface InterviewSession {
  id: string;
  interviewLocationId: string;
  status: InterviewSessionStatus;
  orchestratorState: OrchestratorState;
  sessionMetadata: SessionMetadata;
  startedAt: Date | null;
  completedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Orchestrator state (from JSONB)
 */
export interface OrchestratorState {
  phase?: InterviewPhase;
  currentQuestionIndex?: number;
  assessments?: Assessment[];
  langGraphThreadId?: string;
  [key: string]: unknown;
}

/**
 * Session metadata (from JSONB)
 */
export interface SessionMetadata {
  candidateId?: string;
  jobId?: string;
  pusherChannel?: string;
  source?: string;
  language?: string;
  [key: string]: unknown;
}

/**
 * Assessment result
 */
export interface Assessment {
  questionId: string;
  score: number;
  feedback: string;
  timestamp: string;
}

/**
 * API Request/Response DTOs
 */
export interface StartInterviewRequest {
  interviewLocationId: string;
  sessionId?: string;
  candidateId?: string;
  jobgetClickId?: string;
  invitationToken?: string;
  metadata?: Record<string, unknown>;
}

export interface StartInterviewResponse {
  sessionId: string;
  pusherChannel: string;
  status: InterviewSessionStatus;
  // SC-127: session-scoped token the SPA uses to authenticate subsequent calls.
  sessionToken: string;
  canEditPreviousResponse?: boolean;
}

/**
 * SRN-715: classification of the question this turn relates to. Mirrors
 * the CHECK constraint on `interview_session_message.question_type`.
 */
export type InterviewMessageQuestionType =
  | "identifying"
  | "basic"
  | "assessment"
  | "qualifying"
  | "intro"
  | "conclusion"
  | "followup"
  | "other";

/** SRN-715: how the user answered. */
export type InterviewMessageResponseFormat =
  "written" | "voice" | "choice" | "skipped";

export interface SendMessageRequest {
  sessionId: string;
  /** Stable ID used to make a queued message retry idempotent. */
  messageId?: string;
  /** Local benchmark retry ID used to create a distinct queue processing attempt. */
  benchmarkRetryId?: string;
  content?: string;
  selectedOptionIds?: string[];
  /**
   * SRN-715: per-turn metadata echoed from the latest Pusher event so the
   * persisted user-turn row in `interview_session_message` is
   * self-describing for the processing worker.
   */
  interviewQuestionId?: string;
  questionType?: InterviewMessageQuestionType;
  responseFormat?: InterviewMessageResponseFormat;
  /**
   * AS-1844 — for voice answers the FE already holds the recording's s3:// key
   * (`voiceUrls.audioUrl`). Pass it explicitly so `interview_session_message.audio_s3_key`
   * gets populated alongside `content`; downstream voice surfaces
   * (`session_assessment_result.audio_s3_key`, competency-level PLAY RECORDING)
   * read off that column.
   */
  audioS3Key?: string;
}

export interface SendMessageResponse {
  messageId: string;
  status: string;
}

export interface PreviousQuestionResponse {
  canEditPreviousResponse: boolean;
  messageId: string;
  messageOrder: number;
  questionId: string | null;
  questionType?: InterviewMessageQuestionType | null;
  prompt: string;
  inputType: InterviewQuestionInputType;
  options?: InputOption[];
  presentationLocalized: boolean;
  content: string;
  selectedOptionIds?: string[];
  answerResponseFormat?: InterviewMessageResponseFormat | null;
  responseFormat?: ResponseFormat | null;
  audioS3Key?: string | null;
  audioPlaybackUrl?: string | null;
  uploadUrl?: string;
  audioUrl?: string;
  canSkip?: boolean;
  recordingDurationSeconds?: number;
}

export interface EditPreviousResponseRequest extends SendMessageRequest {
  messageId: string;
}

export interface EndInterviewResponse {
  sessionId: string;
  status: InterviewSessionStatus;
  summary?: string;
}

/**
 * Pusher event data from the interview orchestrator worker
 */
export interface InterviewerPusherEventData {
  sessionId: string;
  /** Development-only correlation for local benchmark response annotations. */
  benchmarkTurnId?: string;
  messageId?: string;
  content?: string;
  status?: InterviewSessionStatus;
  phase?: InterviewPhase;
  error?: string;
  /** The input type for the next question */
  inputType?: InterviewQuestionInputType;
  /** Options for choice-type questions */
  options?: InputOption[];
  /** Candidate-selected language for multilingual sessions */
  language?: string;
  /** The ID of the current question */
  interviewQuestionId?: string;
  /** SRN-715: classification of the current question (echoed back on send) */
  questionType?: InterviewMessageQuestionType;
  /** Presigned S3 upload URL for voice questions */
  uploadUrl?: string;
  /** S3 key returned alongside the upload URL */
  audioUrl?: string;
  /** Voice response format (voice_required, voice_recommended, written) */
  responseFormat?: ResponseFormat;
  /** Whether the candidate can skip a voice question */
  canSkip?: boolean;
  /** Maximum recording duration in seconds */
  recordingDurationSeconds?: number;
  /** AS-1564: 1-based progress position emitted from QUESTIONS phase onward. */
  currentQuestionNumber?: number;
  /** AS-1564: total counted questions for the interview. */
  totalQuestions?: number;
}

/**
 * Pusher event payloads (legacy format - kept for compatibility)
 */
export interface PusherMessageEvent {
  type: "message" | "status_update" | "error";
  data: {
    sessionId: string;
    messageId?: string;
    role?: MessageRole;
    content?: string;
    phase?: InterviewPhase;
    status?: InterviewSessionStatus;
    error?: string;
    questionId?: string;
  };
  timestamp: string;
}

/**
 * Interview configuration
 */
export interface InterviewConfig {
  id: string;
  companyId: string;
  type: InterviewType;
  status: InterviewStatus;
  internalName: string | null;
  openingStatement: string | null;
  closingStatement: string | null;
  questionGroups: InterviewQuestionGroup[];
  questions: InterviewQuestion[];
  locations: InterviewLocation[];
}

/**
 * Interview question group
 */
export interface InterviewQuestionGroup {
  id: string;
  interviewId: string;
  type: string;
  name: string;
  displayName: string | null;
  description: string | null;
  sortOrder: number;
}

/**
 * Interview question
 */
export interface InterviewQuestion {
  id: string;
  interviewId: string;
  interviewQuestionGroupId: string | null;
  questionText: string;
  internalLabel: string | null;
  inputType: InterviewQuestionInputType;
  sortOrder: number;
  maxFollowups: number;
  options: InterviewQuestionOption[];
}

/**
 * Interview question option
 */
export interface InterviewQuestionOption {
  id: string;
  interviewQuestionId: string;
  optionText: string;
  optionValue: string;
  sortOrder: number;
}

/**
 * Interview location
 */
export interface InterviewLocation {
  id: string;
  interviewId: string;
  name: string;
  /** AS-1851: company name backing the interview header. Always returned by
   * the backend — typed as `string | null` (not optional) so consumers handle
   * the null branch explicitly. */
  companyName: string | null;
  /** AS-1851: job title backing the interview header. Always returned by
   * the backend — see companyName for the typing rationale. */
  jobTitle: string | null;
}

/**
 * Resume session types (SRN-713)
 */
export interface ResumeSessionResponse {
  sessionId: string;
  screenerLocationId: string;
  company: string;
  jobTitle: string;
  candidateEmail: string | null;
  candidateName: string | null;
  sessionStatus: "active" | "completed" | "expired";
  language: string | null;
}

export interface ContinueResumeResponse {
  sessionId: string;
  pusherChannel: string;
  phase: string | null;
  // SC-127: session-scoped token for the reconnected session.
  sessionToken: string;
  canEditPreviousResponse?: boolean;
}

export interface RestartResumeResponse {
  sessionId: string;
  pusherChannel: string;
  // SC-127: session-scoped token for the new session.
  sessionToken: string;
}

export interface SaveProgressResponse {
  sessionId: string;
  resumeLink: string;
  emailSent: boolean;
  saveCount: number;
}

export interface SessionMessage {
  id: string;
  role: string;
  content: string;
  messageOrder: number;
  interviewQuestionId: string | null;
  createdAt: string;
  inputType?: string;
  /** Answer-row response format for user messages. */
  answerResponseFormat?: InterviewMessageResponseFormat | null;
  /** Stored audio key for prior voice-answer user messages. */
  audioS3Key?: string | null;
  options?: Array<{
    id: string;
    optionText: string;
    optionValue?: string;
    sortOrder: number;
  }>;
  /** Presigned S3 upload URL for the current voice question (only on the last assistant message). */
  uploadUrl?: string;
  /** S3 key returned alongside the upload URL. */
  audioUrl?: string;
  /** Voice response format for this message/question. */
  responseFormat?: string;
  /** Whether the candidate can skip the current voice question. */
  canSkip?: boolean;
  /** Maximum recording duration in seconds for the current voice question. */
  recordingDurationSeconds?: number;
  /** AS-1564: 1-based progress position (only on the last assistant message). */
  currentQuestionNumber?: number;
  /** AS-1564: total counted questions (only on the last assistant message). */
  totalQuestions?: number;
}
